import {
  COGNITO_CLIENT_ID,
  COGNITO_REGION,
  COGNITO_USER_POOL_ID,
} from "@/config/environment";

const cognitoEndpoint = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`;

const missingConfigLabels = {
  clientId: "VITE_COGNITO_CLIENT_ID",
  region: "VITE_COGNITO_REGION",
  userPoolId: "VITE_COGNITO_USER_POOL_ID",
} as const;

const unsupportedChallengeNames = new Set([
  "MFA_SETUP",
  "SMS_MFA",
  "SOFTWARE_TOKEN_MFA",
  "CUSTOM_CHALLENGE",
]);

export const PASSWORD_REQUIREMENTS = [
  {
    id: "minimumLength",
    label: "Al menos 8 caracteres",
    test: (password: string) => password.length >= 8,
  },
  {
    id: "number",
    label: "Al menos 1 número",
    test: (password: string) => /\d/.test(password),
  },
  {
    id: "specialCharacter",
    label: "Al menos 1 carácter especial",
    test: (password: string) =>
      /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password),
  },
  {
    id: "uppercase",
    label: "Al menos 1 letra mayúscula",
    test: (password: string) => /[A-Z]/.test(password),
  },
  {
    id: "lowercase",
    label: "Al menos 1 letra minúscula",
    test: (password: string) => /[a-z]/.test(password),
  },
] as const;

export const PASSWORD_REQUIREMENTS_MESSAGE = `La nueva contraseña debe contener: ${PASSWORD_REQUIREMENTS.map(
  (requirement) => requirement.label.toLowerCase(),
).join(", ")}.`;

export const getUnmetPasswordRequirements = (password: string) =>
  PASSWORD_REQUIREMENTS.filter((requirement) => !requirement.test(password));

export interface UserData {
  name: string;
  email: string;
  photo: string;
  id: string;
  groups: string[];
}

export interface AuthCredentials {
  username: string;
  password: string;
}

interface CognitoInitiateAuthResponse {
  AuthenticationResult?: {
    AccessToken?: string;
    ExpiresIn?: number;
    IdToken?: string;
    RefreshToken?: string;
    TokenType?: string;
  };
  ChallengeName?: string;
  Session?: string;
}

interface CognitoCodeDeliveryDetails {
  AttributeName?: string;
  DeliveryMedium?: string;
  Destination?: string;
}

interface CognitoForgotPasswordResponse {
  CodeDeliveryDetails?: CognitoCodeDeliveryDetails;
}

interface CognitoErrorResponse {
  __type?: string;
  message?: string;
  Message?: string;
}

export interface CognitoTokenSet {
  type: "TOKEN_SET";
  accessToken: string;
  expiresIn: number;
  idToken: string;
  refreshToken?: string;
  tokenType: string;
}

export interface ForgotPasswordDeliveryDetails {
  attributeName?: string;
  deliveryMedium?: string;
  destination?: string;
}

export interface NewPasswordChallenge {
  type: "NEW_PASSWORD_REQUIRED";
  session: string;
}

export class CognitoAuthError extends Error {
  code: string;
  challengeName?: string;

  constructor(message: string, code: string, challengeName?: string) {
    super(message);
    this.name = "CognitoAuthError";
    this.code = code;
    this.challengeName = challengeName;
  }
}

const getMissingCognitoConfig = () => {
  const missing: string[] = [];

  if (!COGNITO_CLIENT_ID) {
    missing.push(missingConfigLabels.clientId);
  }

  if (!COGNITO_REGION) {
    missing.push(missingConfigLabels.region);
  }

  return missing;
};

const getCognitoConfig = () => {
  const missing = getMissingCognitoConfig();

  if (missing.length > 0) {
    throw new CognitoAuthError(
      `Missing Cognito configuration: ${missing.join(", ")}`,
      "MISSING_COGNITO_CONFIG",
    );
  }

  return {
    clientId: COGNITO_CLIENT_ID as string,
    region: COGNITO_REGION,
    userPoolId: COGNITO_USER_POOL_ID,
  };
};

const normalizeErrorCode = (
  response: Response,
  payload: CognitoErrorResponse,
) => {
  const getLastSegment = (value?: string) => {
    if (!value) {
      return undefined;
    }

    const hashSegments = value.split("#");
    const hashValue = hashSegments[hashSegments.length - 1];
    const colonSegments = hashValue.split(":");
    return colonSegments[0];
  };

  const headerError = response.headers.get("x-amzn-errortype")?.split(":")[0];
  const bodyError = payload.__type;

  return (
    getLastSegment(headerError) ||
    getLastSegment(bodyError) ||
    "UnknownCognitoError"
  );
};

const normalizeCognitoError = (code: string) => {
  switch (code) {
    case "NotAuthorizedException":
      return "Credenciales inválidas. Revisa tu correo y contraseña.";
    case "UserNotFoundException":
      return "No existe una cuenta con ese correo.";
    case "UserNotConfirmedException":
      return "Tu usuario no está confirmado todavía.";
    case "PasswordResetRequiredException":
      return "Tu usuario requiere restablecer la contraseña.";
    case "CodeMismatchException":
      return "El código ingresado no es válido.";
    case "ExpiredCodeException":
      return "El código expiró. Solicita uno nuevo.";
    case "InvalidPasswordException":
      return PASSWORD_REQUIREMENTS_MESSAGE;
    case "InvalidParameterException":
      return "Revisa los datos ingresados e inténtalo nuevamente.";
    case "LimitExceededException":
    case "TooManyFailedAttemptsException":
    case "TooManyRequestsException":
      return "Se alcanzó el límite de intentos. Intenta nuevamente en unos minutos.";
    case "MISSING_COGNITO_CONFIG":
      return "La configuración de Cognito está incompleta.";
    default:
      return "No fue posible iniciar sesión con Cognito.";
  }
};

const requestCognito = async <TResponse>(
  target:
    | "ConfirmForgotPassword"
    | "ForgotPassword"
    | "InitiateAuth"
    | "RespondToAuthChallenge",
  body: Record<string, unknown>,
) => {
  const response = await fetch(cognitoEndpoint, {
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${target}`,
    },
    method: "POST",
  });

  const data = (await response.json()) as TResponse | CognitoErrorResponse;

  if (!response.ok) {
    const code = normalizeErrorCode(response, data as CognitoErrorResponse);
    throw new CognitoAuthError(normalizeCognitoError(code), code);
  }

  return data as TResponse;
};

const normalizeTokenSet = (
  response: CognitoInitiateAuthResponse,
  refreshTokenFallback?: string,
): CognitoTokenSet => {
  const authResult = response.AuthenticationResult;

  if (
    !authResult?.IdToken ||
    !authResult.AccessToken ||
    !authResult.ExpiresIn ||
    !authResult.TokenType
  ) {
    throw new CognitoAuthError(
      "Cognito no devolvió un set de tokens válido.",
      "INVALID_TOKEN_SET",
    );
  }

  return {
    type: "TOKEN_SET",
    accessToken: authResult.AccessToken,
    expiresIn: authResult.ExpiresIn,
    idToken: authResult.IdToken,
    refreshToken: authResult.RefreshToken || refreshTokenFallback,
    tokenType: authResult.TokenType,
  };
};

const throwUnsupportedChallenge = (challengeName?: string) => {
  if (!challengeName || !unsupportedChallengeNames.has(challengeName)) {
    return;
  }

  throw new CognitoAuthError(
    "Este flujo de autenticación de Cognito todavía no está soportado en esta versión.",
    "UNSUPPORTED_COGNITO_CHALLENGE",
    challengeName,
  );
};

export { getMissingCognitoConfig };

export const login = async ({
  username,
  password,
}: AuthCredentials): Promise<CognitoTokenSet | NewPasswordChallenge> => {
  const { clientId } = getCognitoConfig();

  const response = await requestCognito<CognitoInitiateAuthResponse>(
    "InitiateAuth",
    {
      AuthFlow: "USER_PASSWORD_AUTH",
      AuthParameters: {
        PASSWORD: password,
        USERNAME: username,
      },
      ClientId: clientId,
    },
  );

  if (response.ChallengeName === "NEW_PASSWORD_REQUIRED" && response.Session) {
    return { type: "NEW_PASSWORD_REQUIRED", session: response.Session };
  }

  throwUnsupportedChallenge(response.ChallengeName);

  return normalizeTokenSet(response);
};

export const completeNewPassword = async ({
  username,
  newPassword,
  session,
}: {
  username: string;
  newPassword: string;
  session: string;
}): Promise<CognitoTokenSet> => {
  const { clientId } = getCognitoConfig();

  const response = await requestCognito<CognitoInitiateAuthResponse>(
    "RespondToAuthChallenge",
    {
      ChallengeName: "NEW_PASSWORD_REQUIRED",
      ClientId: clientId,
      ChallengeResponses: {
        NEW_PASSWORD: newPassword,
        USERNAME: username,
      },
      Session: session,
    },
  );

  return normalizeTokenSet(response);
};

export const refreshToken = async (
  refreshTokenValue: string,
): Promise<CognitoTokenSet> => {
  const { clientId } = getCognitoConfig();

  const response = await requestCognito<CognitoInitiateAuthResponse>(
    "InitiateAuth",
    {
      AuthFlow: "REFRESH_TOKEN_AUTH",
      AuthParameters: {
        REFRESH_TOKEN: refreshTokenValue,
      },
      ClientId: clientId,
    },
  );

  throwUnsupportedChallenge(response.ChallengeName);

  return normalizeTokenSet(response, refreshTokenValue);
};

export const forgotPassword = async ({
  username,
}: Pick<
  AuthCredentials,
  "username"
>): Promise<ForgotPasswordDeliveryDetails> => {
  const { clientId } = getCognitoConfig();

  const response = await requestCognito<CognitoForgotPasswordResponse>(
    "ForgotPassword",
    {
      ClientId: clientId,
      Username: username,
    },
  );

  return {
    attributeName: response.CodeDeliveryDetails?.AttributeName,
    deliveryMedium: response.CodeDeliveryDetails?.DeliveryMedium,
    destination: response.CodeDeliveryDetails?.Destination,
  };
};

export const confirmForgotPassword = async ({
  password,
  username,
  verificationCode,
}: {
  password: string;
  username: string;
  verificationCode: string;
}) => {
  const { clientId } = getCognitoConfig();

  await requestCognito<Record<string, never>>("ConfirmForgotPassword", {
    ClientId: clientId,
    ConfirmationCode: verificationCode,
    Password: password,
    Username: username,
  });
};

export const getUserData = async (): Promise<{
  data: { user: UserData; status: string };
}> => {
  throw new CognitoAuthError(
    "La carga remota de perfil ya no está habilitada en este flujo.",
    "PROFILE_FETCH_NOT_SUPPORTED",
  );
};
